'use client';

import { lazy, Suspense } from 'react';
import { Box } from '@mildang/styled-system/jsx';
import { useChatSidebarStore } from '@ui/features/mildang-ai/stores/chatStore';
import { ChatFloatingButton } from '@ui/features/mildang-ai/ui/ChatFloatingButton/ChatFloatingButton';
import useMe from '@ui/hooks/useMe';
import DataProgress from '@mildang/design-system-legacy/esm/components/molecules/progress/DataProgress';
import { cva } from '@mildang/styled-system/css';
import { useParams, usePathname } from 'next/navigation';
import { NotificationClick } from 'hooks/useNotificationClick';
import { RegisterFcmToken } from '@ui/features/mildang-ai/mutations/registerFcmToken/useRegisterFcmToken';
import { ConfirmNotificationPermission } from 'hooks/useConfirmNotificationPermission';

const ChatSidebar = lazy(() => import('@ui/features/mildang-ai/ui/ChatSidebar/ChatSidebar'));
const InAppNotification = lazy(() =>
  import('hooks/useInAppNotification').then((mod) => ({ default: mod.InAppNotification })),
);

const buttonWrapperStyle = cva({
  base: {
    position: 'fixed',
    bottom: 'calc(16px + var(--safe-area-inset-bottom))',
    right: '16px',
    transform: 'translateX(0)',
    transition: 'transform 0.2s ease-in-out',
  },
  variants: {
    isSidebarOpen: {
      true: {
        right: '-325px',
        transform: 'translateX(-360px)',
      },
    },
    isLearningScreen: {
      true: {
        bottom: 'calc(76px + var(--safe-area-inset-bottom))',
        md: {
          bottom: 'calc(16px + var(--safe-area-inset-bottom))',
        },
      },
    },
    // AI 튜터 래퍼: 튜터 자체 메시지 입력창(하단 고정)을 가리지 않도록 위로 올림
    isAiTutorScreen: {
      true: {
        bottom: 'calc(88px + var(--safe-area-inset-bottom))',
      },
    },
  },
});

const sidebarWrapperStyle = cva({
  base: {
    overflow: 'hidden',
    borderLeft: '1px solid token(colors.common.5)',
    width: '0',
    transition: 'transform 0.2s ease-in-out',
    transform: 'translateX(100%)',
  },
  variants: {
    isSidebarOpen: {
      true: {
        width: '360px',
        transform: 'translateX(0%)',
      },
    },
  },
});

const GlobalLayout = ({ children }: { children: React.ReactNode }) => {
  const params = useParams();
  const pathname = usePathname();
  const { me } = useMe();
  const { isSidebarOpen } = useChatSidebarStore();

  if (!me) {
    return children;
  }

  // 학습하기 페이지인 경우 smiId를 전달
  const smiId = (() => {
    const _smiId = params?.['studyModuleInstanceId'];
    if (typeof _smiId === 'string') {
      return _smiId;
    }
    return undefined;
  })();

  return (
    <>
      <Box display="flex" height="var(--fullHeight, 100vh)" position="relative" overflow="hidden">
        <Box flex={1}>{children}</Box>

        <Box
          className={buttonWrapperStyle({
            isSidebarOpen,
            isLearningScreen: !!smiId,
            isAiTutorScreen: pathname === '/math-ai-tutor',
          })}
        >
          <ChatFloatingButton channelMemberType="STUDENT" />
        </Box>

        <Box className={sidebarWrapperStyle({ isSidebarOpen })}>
          <Suspense fallback={<DataProgress />}>
            <ChatSidebar channelMemberType="STUDENT" smiId={smiId} />
          </Suspense>
        </Box>
      </Box>
      <RegisterFcmToken />
      <NotificationClick />
      <Suspense fallback={null}>
        <InAppNotification />
      </Suspense>
      <ConfirmNotificationPermission />
    </>
  );
};

export default GlobalLayout;
